import { FC, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Header } from 'components';
import Table from 'components/tables/table';
import { PlusIcon, EyeIcon } from '@heroicons/react/24/outline';
import useApi from 'utils/use-api';
import useTableData from 'utils/use-table-data';
import { AccessRequest } from 'types/access-request';
import CreateRequestModal from './create';
import ViewRequestModal from './view';
import dayjs from 'dayjs';
import { useUrlState } from 'utils';

const AccessRequestsPage: FC = () => {
  useEffect(() => {
    document.title = 'Access Requests';
  }, []);

  const api = useApi();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<AccessRequest | null>(null);
  const [skip, setSkip] = useState(0);
  const [limit] = useUrlState<number>('pageSize');

  const {
    data: requestsResponse,
    isLoading,
    isRefetching,
    refetch,
  } = useQuery({
    queryKey: ['access-requests', skip, limit],
    queryFn: async () => {
      const response = await api.get('/access-requests', {
        params: { skip, limit: limit || 10 },
      });
      return response.data;
    },
  });

  const requestsData = useTableData({
    rows: requestsResponse?.rows || [],
    count: requestsResponse?.total || 0,
  });

  const handleSearch = () => {
    refetch();
  };

  const handleViewRequest = (request: AccessRequest) => {
    setSelectedRequest(request);
    setViewModalOpen(true);
  };

  const handleCreateRequest = () => {
    setCreateModalOpen(true);
  };

  const handleCloseModals = () => {
    setCreateModalOpen(false);
    setViewModalOpen(false);
    setSelectedRequest(null);
  };

  const RequestsTableColumns = () => (
    <tr>
      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
        Request ID
      </th>
      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
        Requester
      </th>
      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
        Category
      </th>
      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
        Assigned To
      </th>
      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
        Status
      </th>
      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
        Created
      </th>
      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
        <span className="sr-only">Actions</span>
      </th>
    </tr>
  );

  const RequestsTableRow = (request: AccessRequest) => {
    const statusColors = {
      open: 'bg-blue-50 text-blue-700 ring-blue-600/20',
      reopened: 'bg-purple-50 text-purple-700 ring-purple-600/20',
      processing: 'bg-yellow-50 text-yellow-700 ring-yellow-600/20',
      more_info: 'bg-orange-50 text-orange-700 ring-orange-600/20',
      awaiting_approval: 'bg-cyan-50 text-cyan-700 ring-cyan-600/20',
      complete: 'bg-green-50 text-green-700 ring-green-600/20',
      closed: 'bg-gray-50 text-gray-700 ring-gray-600/20',
    } as const;

    const statusLabels = {
      open: 'Open',
      reopened: 'Re-Opened',
      processing: 'Processing',
      more_info: 'More Info Needed',
      awaiting_approval: 'Awaiting Approval',
      complete: 'Complete',
      closed: 'Closed',
    } as const;

    const categoryLabels: Record<string, string> = {
      hardware: 'Hardware',
      system_access: 'System Access',
      software: 'Software',
      email: 'Email Account',
      communication: 'Communication Tools',
      training: 'Training Access',
      other: 'Other',
    };

    const formatDate = (dateString: string) => {
      try {
        return dayjs(dateString).format('MMM DD, YYYY');
      } catch {
        return dateString;
      }
    };

    return (
      <tr className="hover:bg-gray-50">
        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">#{request.id}</td>
        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
          <div className="flex flex-col">
            <span className="font-medium text-gray-900">{request.requesterName}</span>
            <span className="text-xs text-gray-500">{request.requesterEmail}</span>
          </div>
        </td>
        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
          <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">
            {categoryLabels[request.category] || request.category}
          </span>
        </td>
        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
          <div className="flex items-center gap-x-2">
            <span className="font-medium text-gray-900">{request.assigneeName}</span>
            <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
              {request.assigneeType === 'group' ? 'Group' : 'User'}
            </span>
          </div>
        </td>
        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
          <span
            className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${statusColors[request.status]}`}
          >
            {statusLabels[request.status]}
          </span>
        </td>
        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{formatDate(request.createdAt)}</td>
        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
          <button onClick={() => handleViewRequest(request)} className="inline-flex items-center gap-x-1 text-primary-600 hover:text-primary-900">
            <EyeIcon className="h-4 w-4" />
            View
          </button>
        </td>
      </tr>
    );
  };

  return (
    <main className="flex-1 flex flex-col overflow-hidden h-screen bg-gray-50">
      <Header
        renderActions={() => (
          <button
            type="button"
            onClick={handleCreateRequest}
            className="inline-flex items-center gap-x-2 rounded-md bg-primary-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
          >
            <PlusIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
            New Request
          </button>
        )}
      />
      <div className="flex flex-1 overflow-y-auto">
        <div className="flex-1 min-h-full mx-auto p-4 sm:p-6 overflow-y-auto bg-gray-50">
          <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-12 lg:gap-8">
            <div className="col-span-12">
              <Table
                title="Access Requests"
                data={requestsData || { rows: [], total: 0, page: 1, pageSize: limit || 10, totalPages: 0 }}
                loading={isLoading}
                isRefetching={isRefetching}
                skip={skip}
                limit={limit || 10}
                setSkip={setSkip}
                showTableHeader={true}
                hasSearch={true}
                onSearchSubmit={handleSearch}
                refetch={refetch}
                renderColumns={RequestsTableColumns}
                renderItem={RequestsTableRow}
              />
            </div>
          </div>
        </div>
      </div>

      {createModalOpen && (
        <CreateRequestModal
          open={createModalOpen}
          setOpen={setCreateModalOpen}
          onSuccess={() => {
            refetch();
            handleCloseModals();
          }}
        />
      )}

      {viewModalOpen && selectedRequest && (
        <ViewRequestModal
          open={viewModalOpen}
          setOpen={setViewModalOpen}
          request={selectedRequest}
          onSuccess={() => {
            refetch();
          }}
        />
      )}
    </main>
  );
};

export default AccessRequestsPage;
